import { repairImageKitUrl } from '@dupe/common/constants'
import { ArrowLeft, ArrowRight, ArrowUpRight, ImageOff } from 'lucide-react'
import Link from 'next/link'
import type { CSSProperties } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { cn } from '~/src/lib/utils'
import type {
  HubAnchorLinkListItem,
  HubAnchorLinkListSection,
  HubReportRef,
  HubSectionComponentProps,
} from './types'

const SCROLL_AMOUNT_RATIO = 0.82
const carouselStyle = {
  '--evidence-image-height': 'calc(var(--evidence-card-width) * 10 / 16)',
} as CSSProperties & Record<string, string>

function getReportSlugFromHref(href: string): string | null {
  const path = getRawReportHref(href)
  const match = /^\/reports\/([^/]+)\/?$/.exec(path)
  if (!match?.[1]) return null

  try {
    return decodeURIComponent(match[1])
  } catch {
    return match[1]
  }
}

function getRawReportHref(href: string): string {
  const path = href.split(/[?#]/)[0] ?? href
  return path.endsWith('/') && path !== '/' ? path.slice(0, -1) : path
}

function resolveReport(
  href: string,
  reportsById: Record<string, HubReportRef>
): HubReportRef | null {
  const slug = getReportSlugFromHref(href)
  if (!slug) return null

  return (
    Object.values(reportsById).find(
      (report) =>
        report.slug === slug ||
        getRawReportHref(report.href) === `/reports/${slug}`
    ) ?? null
  )
}

interface EvidenceCard {
  href: string
  report: HubReportRef | null
  title: string
  description?: string | null
}

function EvidenceCarouselCard({ card }: { card: EvidenceCard }) {
  const { href, report, title, description } = card
  const imageUrl = report?.imageUrl ?? null

  return (
    <li className='w-[280px] shrink-0 snap-start md:w-[360px]'>
      <Link
        href={href}
        className='group flex h-full min-h-[360px] flex-col overflow-hidden rounded-2xl border border-ds-border-primary bg-ds-bg-primary text-left transition-[box-shadow,border-color] duration-300 hover:border-ds-text-primary md:hover:shadow-maxx-dupe-hover'
      >
        <div className='relative aspect-[16/10] w-full overflow-hidden bg-maxx-warm'>
          {imageUrl ? (
            <img
              src={repairImageKitUrl(imageUrl)}
              alt={report?.title ?? title}
              loading='lazy'
              className='h-full w-full object-cover mix-blend-darken transition-transform duration-300 group-hover:scale-[1.04] dark:mix-blend-normal'
            />
          ) : (
            <div className='flex h-full w-full items-center justify-center bg-ds-bg-secondary'>
              <ImageOff
                className='size-9 text-ds-fg-quaternary'
                strokeWidth={1.5}
              />
            </div>
          )}
        </div>

        <div className='flex flex-1 flex-col'>
          <div className='flex flex-1 flex-col gap-2 px-4 pb-4 pt-4'>
            <h3 className='line-clamp-2 text-xl font-semibold leading-7 text-ds-text-primary'>
              {title}
            </h3>
            {description && (
              <p className='line-clamp-3 text-base leading-6 text-ds-text-secondary'>
                {description}
              </p>
            )}
          </div>

          <div className='mt-auto border-t border-ds-border-primary p-4'>
            <span className='flex h-11 w-full items-center justify-center gap-2 rounded-full bg-ds-text-primary text-base font-medium text-ds-bg-primary transition-opacity group-hover:opacity-90'>
              Open report
              <ArrowUpRight className='size-4' aria-hidden />
            </span>
          </div>
        </div>
      </Link>
    </li>
  )
}

function buildEvidenceCards(
  section: HubAnchorLinkListSection,
  reportsById: Record<string, HubReportRef>
): EvidenceCard[] {
  const cards: EvidenceCard[] = []
  const seenReports = new Set<string>()

  const addReportCard = (
    report: HubReportRef | null,
    link?: HubAnchorLinkListItem
  ) => {
    const href = report?.href ?? (link ? getRawReportHref(link.href) : null)
    if (!href) return

    const key = report?.reportId ?? getReportSlugFromHref(href) ?? href
    if (seenReports.has(key)) return
    seenReports.add(key)

    cards.push({
      href: getRawReportHref(href),
      report,
      title: report?.title ?? link?.label ?? 'Report',
      description: report?.excerpt ?? link?.description ?? null,
    })
  }

  for (const link of section.links) {
    addReportCard(resolveReport(link.href, reportsById), link)
  }

  for (const report of Object.values(reportsById)) {
    addReportCard(report)
  }

  return cards
}

export function HubAnchorLinkList({
  section,
  context,
}: HubSectionComponentProps<HubAnchorLinkListSection>) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [canPrev, setCanPrev] = useState(false)
  const [canNext, setCanNext] = useState(false)

  const cards = useMemo(
    () => buildEvidenceCards(section, context.reportsById),
    [section, context.reportsById]
  )

  const updateArrows = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    setCanPrev(el.scrollLeft > 1)
    setCanNext(el.scrollLeft < el.scrollWidth - el.clientWidth - 1)
  }, [])

  useEffect(() => {
    updateArrows()
    const el = scrollRef.current
    if (!el) return
    el.addEventListener('scroll', updateArrows, { passive: true })
    window.addEventListener('resize', updateArrows)
    return () => {
      el.removeEventListener('scroll', updateArrows)
      window.removeEventListener('resize', updateArrows)
    }
  }, [updateArrows, cards.length])

  if (cards.length === 0) return null

  const scrollByPage = (direction: 1 | -1) => {
    const el = scrollRef.current
    if (!el) return
    el.scrollBy({
      left: Math.round(el.clientWidth * SCROLL_AMOUNT_RATIO) * direction,
      behavior: 'smooth',
    })
  }

  return (
    <section
      id={section.id}
      aria-label={section.title ?? 'Report shortcuts'}
      className='flex min-w-0 flex-col gap-5'
    >
      <div className='max-w-3xl'>
        {section.eyebrow && (
          <p className='text-xs font-semibold uppercase tracking-wide text-ds-text-quaternary'>
            {section.eyebrow}
          </p>
        )}
        {section.title && (
          <h2 className='mt-1 font-display text-2xl font-bold leading-8 text-foreground'>
            {section.title}
          </h2>
        )}
        {section.dek && (
          <p className='mt-2 text-base leading-6 text-foreground-secondary'>
            {section.dek}
          </p>
        )}
      </div>

      <div
        className='relative -mx-4 md:mx-0 md:[--evidence-card-width:360px]'
        style={carouselStyle}
      >
        <div
          ref={scrollRef}
          className='no-scrollbar overflow-x-auto scroll-smooth px-4 pb-4 md:px-0'
        >
          <ul className='flex snap-x snap-mandatory gap-4'>
            {cards.map((card) => (
              <EvidenceCarouselCard key={card.href} card={card} />
            ))}
          </ul>
        </div>

        <div className='pointer-events-none absolute inset-x-0 top-0 hidden h-[var(--evidence-image-height)] md:block'>
          <button
            type='button'
            aria-label='Scroll evidence left'
            onClick={() => scrollByPage(-1)}
            className={cn(
              'pointer-events-auto absolute left-2 top-1/2 z-10 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-ds-bg-primary/95 text-ds-text-primary shadow-md backdrop-blur transition-opacity md:flex md:hover:bg-ds-bg-primary',
              canPrev ? 'opacity-100' : 'pointer-events-none opacity-0'
            )}
          >
            <ArrowLeft className='size-5' aria-hidden />
          </button>
          <button
            type='button'
            aria-label='Scroll evidence right'
            onClick={() => scrollByPage(1)}
            className={cn(
              'pointer-events-auto absolute right-2 top-1/2 z-10 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-ds-bg-primary/95 text-ds-text-primary shadow-md backdrop-blur transition-opacity md:flex md:hover:bg-ds-bg-primary',
              canNext ? 'opacity-100' : 'pointer-events-none opacity-0'
            )}
          >
            <ArrowRight className='size-5' aria-hidden />
          </button>
        </div>
        <div
          aria-hidden
          className='pointer-events-none absolute inset-y-0 right-0 hidden w-10 bg-gradient-to-r from-transparent to-ds-bg-primary md:block'
        />
      </div>
    </section>
  )
}
